import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  Collapse,
  List,
  Box,
} from '@mui/material';
import { getIconComponent, PinIcon } from '../../utils/icons';
import type { MenuItem } from '../../types';
import { isIFrameApp, isNewTabApp } from '@/services/utils';
import { getMenuItemTestId } from '../../constants';
import { useTruncated } from '../../hooks/useTruncated';

interface NavigationItemProps {
  readonly item: MenuItem;
  readonly onItemClick?: (item: MenuItem) => void;
  readonly nested?: boolean;
}

export function NavigationItem({ item, onItemClick, nested = false }: NavigationItemProps) {
  const location = useLocation();
  const IconComponent = getIconComponent(item.icon);
  const isActive = location.pathname === item.path;
  const NewTabIconComponent = getIconComponent('NewTab');
  const ChevronRightIcon = getIconComponent('ChevronRight');
  const ExpandMoreIcon = getIconComponent('ExpandMore');

  const [isExpanded, setIsExpanded] = useState(false);
  const isGroup = item.isGroup && item.children && item.children.length > 0;

  const labelRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useTruncated(labelRef);

  const handleClick = (e: React.MouseEvent) => {
    if (isGroup) {
      // For group items, toggle expansion and prevent navigation
      e.preventDefault();
      setIsExpanded(!isExpanded);
    } else if (onItemClick) {
      onItemClick(item);
    }
  };

  // Logic for determining the "to" property:
  // - If it's a group item: use a non-navigable hash
  // - If it's an iframe app: always use the item's path
  // - If it's not an iframe app and openInNewTab is true and externalUrl exists: use the externalUrl
  // - In all other cases: use the item's path
  let to: string;
  if (isGroup) {
    to = item.path; // Will be something like "#group-financials-modules"
  } else if (isIFrameApp(item)) {
    to = item.path;
  } else if (isNewTabApp(item)) {
    to = item.externalUrl;
  } else {
    to = item.path;
  }

  return (
    <>
      <ListItem disablePadding>
        <Tooltip title={isTruncated ? item.label : ''} arrow enterNextDelay={300} placement="right">
          <ListItemButton
            data-testid={getMenuItemTestId(item.id || item.path)}
            component={isGroup ? 'div' : Link}
            to={isGroup ? undefined : to}
            aria-expanded={isGroup ? isExpanded : undefined}
            target={isNewTabApp(item) ? '_blank' : undefined}
            onClick={handleClick}
            disableRipple
            selected={isActive && !isGroup}
            sx={(theme) => ({
              px: 2,
              py: 1,
              minHeight: 40,
              borderRadius: 2,
              mx: 1,
              ...(nested && {
                pl: 7, // Additional left padding for nested items
              }),
              ...(isActive &&
                !isGroup && {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.dark}`,
                    outlineOffset: '2px',
                  },
                }),
              ...(!isActive && {
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              }),
            })}
          >
            {!nested && (
              <ListItemIcon sx={{ minWidth: 40 }}>
                <IconComponent
                  aria-hidden
                  sx={{
                    fontSize: 20,
                    color: isActive ? 'primary.main' : 'text.secondary',
                  }}
                />
              </ListItemIcon>
            )}
            <ListItemText
              disableTypography
              primary={
                <Typography
                  ref={labelRef}
                  component="span"
                  noWrap
                  sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    color: isActive ? 'text.main' : 'text.primary',
                  }}
                >
                  {item.label}
                </Typography>
              }
              sx={{ flexGrow: item.dotBadge ? 0 : 1, minWidth: 0 }}
            />
            {item.dotBadge && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  flexShrink: 0,
                  ml: 1,
                }}
              />
            )}
            {isGroup || isNewTabApp(item) ? (
              <ListItemIcon sx={{ minWidth: 40 }}>
                {isGroup ? (
                  isExpanded ? (
                    <ExpandMoreIcon
                      aria-hidden
                      sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                      }}
                    />
                  ) : (
                    <ChevronRightIcon
                      aria-hidden
                      sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                      }}
                    />
                  )
                ) : (
                  <NewTabIconComponent
                    aria-hidden
                    sx={{
                      fontSize: 20,
                      color: isActive ? 'primary.main' : 'text.tertiary',
                    }}
                  />
                )}
              </ListItemIcon>
            ) : null}
            {item.isPinned && (
              <PinIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0, ml: 1 }} />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {/* Render children if this is a group item */}
      {isGroup && item.children && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children.map((child) => (
              <NavigationItem key={child.path} item={child} onItemClick={onItemClick} nested />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}
